import React from "react";
import { Button, Group, Title, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Download, FileSpreadsheet, FileText, LayoutDashboard } from "lucide-react";
import dayjs from "dayjs";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { KPICard, ChartCard, StatCard, TOOLTIP_STYLE, chartProps } from "./AnalyticsLayout";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

const generateRevenueData = () => [
  { date: "Jan 1", daily: 15420, monthly: 425620 },
  { date: "Jan 5", daily: 18560, monthly: 450890 },
  { date: "Jan 10", daily: 22340, monthly: 485670 },
  { date: "Jan 15", daily: 19870, monthly: 498340 },
  { date: "Jan 20", daily: 24560, monthly: 520450 },
  { date: "Jan 25", daily: 21450, monthly: 535690 },
  { date: "Jan 30", daily: 25670, monthly: 550120 },
];

const generateUserGrowthData = () => [
  { date: "Week 1", free: 15420, paid: 8450, premium: 3200 },
  { date: "Week 2", free: 18900, paid: 11230, premium: 4120 },
  { date: "Week 3", free: 22340, paid: 14560, premium: 5670 },
  { date: "Week 4", free: 25680, paid: 18340, premium: 7290 },
];

const generateDeviceDistribution = () => [
  { name: "Mobile", value: 65, users: 81523 },
  { name: "Web", value: 20, users: 25084 },
  { name: "TV App", value: 10, users: 12542 },
  { name: "Tablet", value: 5, users: 6271 },
];

const ExecutiveDashboard = () => {
  const [dateRange, setDateRange] = React.useState([dayjs().subtract(30, "days").toDate(), dayjs().toDate()]);

  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  const kpis = {
    totalUsers: 125420,
    userChange: 12.5,
    dau: 45320,
    dauChange: 8.3,
    revenueDaily: 15420.5,
    revenueChange: 22.5,
    revenueMonthly: 425620.0,
    conversionRate: 12.8,
    conversionChange: 4.2,
    totalSubscribers: 85600,
    totalWatchHours: 2456780,
    avgWatchTime: 45.5,
    paidUsers: 45780,
  };

  const revenueData = generateRevenueData();
  const userGrowthData = generateUserGrowthData();
  const deviceData = generateDeviceDistribution();

  return (
    <div className="exec-dashboard">
      <div className="exec-header">
        <div>
          <div className="flex items-center gap-2 text-zinc-500 mb-1">
            <LayoutDashboard size={18} />
            <span className="text-sm">Analytics</span>
          </div>
          <Title order={2} className="text-white font-bold">
            Executive Dashboard
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            Key performance indicators & overview
          </Text>
        </div>
        <Group gap="xs">
          <DatePickerInput
            type="range"
            value={dateRange}
            onChange={setDateRange}
            size="sm"
            maw={280}
          />
          <Button variant="light" size="xs" leftSection={<FileSpreadsheet size={14} />} onClick={() => handleExport("excel")}>
            Excel
          </Button>
          <Button variant="light" size="xs" leftSection={<FileText size={14} />} onClick={() => handleExport("pdf")}>
            PDF
          </Button>
          <Button variant="light" size="xs" leftSection={<Download size={14} />} onClick={() => handleExport("csv")}>
            CSV
          </Button>
        </Group>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard title="Total Users" value={kpis.totalUsers} suffix=" users" change={kpis.userChange} />
        <KPICard title="Daily Active Users" value={kpis.dau} suffix=" users" change={kpis.dauChange} />
        <KPICard title="Daily Revenue" prefix="Tsh " value={kpis.revenueDaily} change={kpis.revenueChange} />
        <KPICard title="Conversion Rate" value={kpis.conversionRate} suffix="%" change={kpis.conversionChange} />
      </div>

      <ChartCard title="Revenue Trend">
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="execRevenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...chartProps.grid} />
              <XAxis dataKey="date" {...chartProps.axis} />
              <YAxis {...chartProps.axis} tickFormatter={(v) => v.toLocaleString()} />
              <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [v?.toLocaleString(), "Revenue"]} />
              <Area type="monotone" dataKey="daily" stroke="#3b82f6" fill="url(#execRevenueGrad)" strokeWidth={2} name="Daily Revenue" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <ChartCard title="User Growth by Tier">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={userGrowthData}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="date" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="free" stackId="a" fill="#3b82f6" name="Free" radius={[0, 0, 0, 0]} />
                <Bar dataKey="paid" stackId="a" fill="#10b981" name="Paid" radius={[0, 0, 0, 0]} />
                <Bar dataKey="premium" stackId="a" fill="#f59e0b" name="Premium" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Platform Distribution">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={deviceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name} ${value}%`}
                  labelLine={false}
                >
                  {deviceData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} stroke="#0a0a0a" strokeWidth={2} />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name, props) => [`${v}%`, props.payload.name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <StatCard title="Total Subscribers" value={kpis.totalSubscribers} suffix=" users" />
        <StatCard title="Total Watch Hours" value={kpis.totalWatchHours} suffix=" hrs" />
        <StatCard title="Avg Watch Time" value={kpis.avgWatchTime} suffix=" min" />
        <StatCard title="Monthly Revenue" prefix="Tsh " value={kpis.revenueMonthly} />
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
